import { useCallback, useRef, useState } from "react";

export const SNAP_SIDE = Object.freeze({ LEFT: "left", RIGHT: "right" });

const DEFAULT_THRESHOLD_PX = 5;

/**
 * Drag an element along the X axis only, then snap it to the left or right
 * half of the viewport on release. The element never moves vertically.
 *
 * While dragging, `offset` is the pointer's horizontal travel — apply it as a
 * `translateX` for feedback. On release `onSnap(side)` receives the physical
 * side (`SNAP_SIDE.LEFT` | `SNAP_SIDE.RIGHT`) the pointer ended on.
 *
 * A press that travels less than `threshold` px is a click, not a drag. When
 * it was a drag, the browser still fires `click` afterwards — guard the
 * element's click handler with `consumeDrag()`, which returns true (and
 * resets) when the click belongs to a drag.
 */
const useHorizontalSnapDrag = ({
  onSnap,
  threshold = DEFAULT_THRESHOLD_PX,
} = {}) => {
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(null);
  const draggedRef = useRef(false);

  const onPointerDown = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) return;
    startXRef.current = event.clientX;
    draggedRef.current = false;
  }, []);

  const onPointerMove = useCallback(
    (event) => {
      if (startXRef.current === null) return;
      const dx = event.clientX - startXRef.current;
      if (!draggedRef.current && Math.abs(dx) < threshold) return;
      if (!draggedRef.current) {
        // Capture only once the press has become a drag: a captured pointer
        // retargets the click to the capturing element, so capturing on
        // pointerdown would stop a plain click reaching the child's onClick.
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }
      draggedRef.current = true;
      setIsDragging(true);
      setOffset(dx);
    },
    [threshold]
  );

  const finish = useCallback(
    (event, snap) => {
      if (startXRef.current === null) return;
      startXRef.current = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      if (snap && draggedRef.current) {
        onSnap?.(
          event.clientX < window.innerWidth / 2
            ? SNAP_SIDE.LEFT
            : SNAP_SIDE.RIGHT
        );
      }
      // A cancelled gesture fires no click, so nothing would consume the flag.
      if (!snap) draggedRef.current = false;
      setIsDragging(false);
      setOffset(0);
    },
    [onSnap]
  );

  const onPointerUp = useCallback((event) => finish(event, true), [finish]);
  const onPointerCancel = useCallback(
    (event) => finish(event, false),
    [finish]
  );

  const consumeDrag = useCallback(() => {
    const dragged = draggedRef.current;
    draggedRef.current = false;
    return dragged;
  }, []);

  return {
    offset,
    isDragging,
    consumeDrag,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
    },
  };
};

export default useHorizontalSnapDrag;
